<template>
  <div class="nr1-observations animated fadeIn">
    <div class="page-hero mb-4">
      <div>
        <p class="hero-eyebrow mb-1">Questionários NR-1</p>
        <h4 class="hero-title mb-1">Observações do Questionário</h4>
        <p class="hero-subtitle mb-0">
          Lista anônima das observações enviadas no encerramento da avaliação.
        </p>
      </div>

      <b-button
        variant="outline-secondary"
        class="btn-back"
        @click="$router.back()"
      >
        <i class="fa fa-arrow-left mr-2"></i> Voltar
      </b-button>
    </div>

    <b-card class="summary-card mb-3" no-body>
      <div class="summary-content">
        <div>
          <p class="summary-label mb-1">Título</p>
          <h5 class="summary-title mb-0">
            {{ questionnaire.title || "Questionário" }}
          </h5>
        </div>

        <div
          class="summary-actions mt-3 mt-md-0 d-flex align-items-center flex-wrap"
        >
          <div class="d-flex align-items-center mr-4">
            <span class="filter-label mr-2">Setor:</span>
            <b-form-select
              v-model="selectedArea"
              :options="areaOptions"
              :disabled="loadingAreas"
              size="sm"
              class="sector-select"
              @change="onAreaChange"
            ></b-form-select>
          </div>

          <div class="summary-chip">
            <span class="summary-chip-label">Total de observações</span>
            <strong class="summary-chip-value">{{ total }}</strong>
          </div>
        </div>
      </div>
    </b-card>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-3 mb-0">Carregando observações...</p>
    </div>

    <b-alert v-else-if="error" show variant="danger">
      <i class="fa fa-exclamation-triangle mr-2"></i>
      {{ error }}
    </b-alert>

    <b-card v-else no-body class="table-shell">
      <b-table
        :items="observations"
        :fields="fields"
        responsive
        hover
        class="mb-0 observations-table"
        show-empty
        empty-text="Nenhuma observação foi registrada para este questionário."
      >
        <template #cell(observation)="data">
          <div class="observation-text">{{ data.value }}</div>
        </template>

        <template #cell(submittedAt)="data">
          <span class="datetime-pill">
            <i class="far fa-clock mr-1"></i>
            {{ formatDateTime(data.value) }}
          </span>
        </template>
      </b-table>

      <div
        class="table-footer d-flex justify-content-between align-items-center"
      >
        <small class="footer-text">
          Mostrando {{ pageStart }}-{{ pageEnd }} de {{ total }}
        </small>

        <b-pagination
          v-model="page"
          :total-rows="total"
          :per-page="limit"
          align="right"
          class="mb-0 modern-pagination"
          @input="loadObservations"
        ></b-pagination>
      </div>
    </b-card>
  </div>
</template>

<script>
import moment from "moment";
import { getQuestionnaire, getObservations, getAreas } from "@/api/nr1";

export default {
  name: "NR1Observations",
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      questionnaire: {},
      observations: [],
      fields: [
        { key: "observation", label: "Observação" },
        { key: "submittedAt", label: "Data/Hora", class: "text-nowrap" },
      ],
      page: 1,
      limit: 20,
      total: 0,
      selectedArea: "Todos",
      areas: [],
      loadingAreas: false,
    };
  },
  computed: {
    areaOptions() {
      return [
        { value: "Todos", text: "Todos os setores" },
        ...this.areas.map((area) => ({
          value: area,
          text: area,
        })),
      ];
    },
    pageStart() {
      if (this.total === 0) {
        return 0;
      }

      return (this.page - 1) * this.limit + 1;
    },
    pageEnd() {
      if (this.total === 0) {
        return 0;
      }

      return Math.min(this.page * this.limit, this.total);
    },
  },
  async mounted() {
    await this.loadPage();
  },
  methods: {
    async loadPage() {
      this.loading = true;
      this.error = null;
      this.loadingAreas = true;

      try {
        const queryParams = {
          company: this.$route.query.company || undefined,
        };

        const [questionnaireResponse, observationsResponse, areasResponse] =
          await Promise.all([
            getQuestionnaire(this.id),
            getObservations(this.id, {
              page: this.page,
              limit: this.limit,
              area:
                this.selectedArea !== "Todos" ? this.selectedArea : undefined,
              company: queryParams.company,
            }),
            getAreas(queryParams),
          ]);

        this.questionnaire = questionnaireResponse.data || {};
        this.applyObservationResponse(observationsResponse.data);
        this.areas = Array.isArray(areasResponse.data)
          ? areasResponse.data
          : [];
      } catch (err) {
        console.error("Error loading NR1 observations:", err);
        this.error =
          err?.response?.data?.message ||
          "Não foi possível carregar as observações deste questionário.";
      } finally {
        this.loading = false;
        this.loadingAreas = false;
      }
    },
    async loadObservations() {
      this.loading = true;
      this.error = null;

      try {
        const response = await getObservations(this.id, {
          page: this.page,
          limit: this.limit,
          area: this.selectedArea !== "Todos" ? this.selectedArea : undefined,
          company: this.$route.query.company || undefined,
        });

        this.applyObservationResponse(response.data);
      } catch (err) {
        console.error("Error loading questionnaire observations:", err);
        this.error =
          err?.response?.data?.message ||
          "Não foi possível carregar as observações.";
      } finally {
        this.loading = false;
      }
    },
    onAreaChange() {
      this.page = 1;
      this.loadObservations();
    },
    applyObservationResponse(payload) {
      this.observations = Array.isArray(payload?.items) ? payload.items : [];
      this.total = Number(payload?.total || 0);
      this.page = Number(payload?.page || this.page);
      this.limit = Number(payload?.limit || this.limit);
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      return moment(value).format("DD/MM/YYYY HH:mm");
    },
  },
};
</script>

<style scoped>
.nr1-observations {
  --surface: #ffffff;
  --surface-alt: #f8fafc;
  --border-soft: #e5e7eb;
  --text-primary: #0f172a;
  --text-muted: #64748b;
  --accent: #2563eb;
  --accent-soft: #dbeafe;
}

.page-hero {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  padding: 1.15rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.hero-eyebrow {
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #475569;
  font-weight: 700;
}

.hero-title {
  color: var(--text-primary);
  font-weight: 700;
}

.hero-subtitle {
  color: var(--text-muted);
  font-size: 0.93rem;
}

.btn-back {
  border-radius: 10px;
  font-weight: 600;
}

.summary-card {
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.summary-content {
  background: var(--surface);
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.summary-label {
  color: var(--text-muted);
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
}

.summary-title {
  color: var(--text-primary);
  font-weight: 700;
}

.summary-chip {
  background: var(--surface-alt);
  border: 1px solid var(--border-soft);
  border-radius: 999px;
  padding: 0.45rem 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.summary-chip-label {
  color: var(--text-muted);
  font-size: 0.8rem;
}

.summary-chip-value {
  min-width: 30px;
  height: 30px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 700;
}

.summary-actions {
  display: flex;
  align-items: center;
}

.filter-label {
  color: var(--text-muted);
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  white-space: nowrap;
}

.sector-select {
  min-width: 180px;
  max-width: 250px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  color: var(--text-primary);
  background-color: var(--surface);
  height: calc(1.5em + 0.5rem + 2px);
  font-size: 0.875rem;
}

.table-shell {
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
}

.observations-table {
  background: var(--surface);
}

.observations-table ::v-deep thead th {
  border-top: 0;
  border-bottom: 1px solid var(--border-soft);
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  color: #475569;
  font-size: 0.72rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  font-weight: 700;
  padding: 0.9rem 1rem;
}

.observations-table ::v-deep tbody td {
  border-top: 1px solid #f1f5f9;
  padding: 0.95rem 1rem;
  vertical-align: top;
}

.observations-table ::v-deep tbody tr:hover td {
  background: #f8fbff;
}

.observation-text {
  white-space: pre-line;
  color: #1e293b;
  line-height: 1.55;
}

.datetime-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 0.25rem 0.65rem;
  font-size: 0.8rem;
  white-space: nowrap;
}

.table-footer {
  border-top: 1px solid var(--border-soft);
  background: #fbfdff;
  padding: 0.9rem 1rem;
}

.footer-text {
  color: var(--text-muted);
}

.modern-pagination ::v-deep .page-link {
  border: 1px solid #dbe2ea;
  color: #334155;
  border-radius: 8px;
  margin: 0 2px;
}

.modern-pagination ::v-deep .page-item.active .page-link {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}

@media (max-width: 768px) {
  .page-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .table-footer {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.7rem;
  }
}
</style>
